<!--
 * @Author: Tahiti
 * @Date: 2025-10-21 14:34:38
 * @LastEditors: Tahiti
 * @LastEditTime: 2025-12-30 17:45:22
 * @Description: 
 * @FilePath: /weryai-web/components/Tool/Text-to-Img-Setting.vue
-->
<template>
    <div
        class="flex flex-col justify-between bg-setting-bg max-h-[inherit] w-full overflow-y-auto rounded-lg p-4 lg:p-6 lg:pb-0 lg:!w-[424px] lg:border-r lg:border-r-[#1D1D20]"
    >
        <div>
            <div class="flex items-center gap-2 justify-end lg:justify-between lg:mb-[30px]">
                <span
                    class="hidden lg:block text-white cursor-default text-base font-semibold md:text-lg"
                >
                    {{ t('menus.textToImage') }}
                </span>
                <!-- <tool-batch-switch
                    v-model="batch_generate"
                    :label="t('batchTools.batchGenerate')"
                ></tool-batch-switch> -->
                <tool-single-img-edit
                    v-model:generate_type="generate_type"
                    :type="props.type"
                ></tool-single-img-edit>
            </div>
            <tool-model-select
                v-if="!batch_generate"
                v-model:model="edit_model"
                :model_list="modelList"
                :type="props.type"
            ></tool-model-select>
            <tool-model-select-mult
                v-else
                v-model:models="edit_model_list"
                :model_list="multiModelList"
                :type="props.type"
            ></tool-model-select-mult>
            <tool-prompt-textarea
                v-model:prompt="edit_prompt"
                :recommend_list="recommend_list"
                v-model:prompt_length_limit="prompt_length_limit"
            ></tool-prompt-textarea>
            <!-- <tool-create-style v-model:style="edit_style"></tool-create-style> -->

            <tool-aspect-radio
                :show_tips="true"
                :label="t('menus.any_aspect_ratio')"
                v-model:aspect_ratio="edit_aspect_ratio"
                :aspect_ratio_list="aspectRatioList"
                :batch_generate="batch_generate"
            ></tool-aspect-radio>
            <!-- 使用 v-show 替代 v-if 减少 CLS 布局偏移 -->
            <tool-video-size
                :show_tips="true"
                :label="t('menus.up_to_4k_resolution')"
                v-show="resolutionList?.length"
                type="IMAGE"
                v-model:video_size="image_size"
                :video_size_list="resolutionList || []"
            ></tool-video-size>
            <tool-image-number
                v-model:output_image_number="edit_output_image_number"
                :num_images="num_images"
                :batch_generate="batch_generate"
            ></tool-image-number>
            <tool-online-search-switch
                v-show="has_web_search"
                v-model:use_web_search="use_web_search"
            ></tool-online-search-switch>
            <tool-public-visibility
                v-model:public_visible="public_visible"
            ></tool-public-visibility>
            <tool-copyright-protection
                v-model:copyright_protection="copyright_protection"
            ></tool-copyright-protection>
        </div>
        <tool-credit-required
            class="lg:sticky lg:bottom-0 lg:py-4 lg:!mb-0 bg-setting-bg"
            :type="props.type"
            :create_btn_disable="props.create_btn_disable"
            workflowType="textToImgBatch"
            :m="cost_model"
            :p="edit_aspect_ratio"
            :n="edit_output_image_number"
            :r1="image_size"
            :loading="props.create_loading"
        ></tool-credit-required>
    </div>
</template>
<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from '~/composables/useI18n'
import type { SettingType } from '~/types'
import { ModelType } from '~/types'
import { useAppStore } from '~/stores/app'

const appStore = useAppStore()
const modelData = computed(() => appStore.modelData)

const { t } = useI18n()
// 模型对比（batch_generate=true）时需要过滤的模型
const MODEL_COMPARE_EXCLUDE_LIST = [
    'DREAMINA_3',
    'DREAMINA_4',
    'MIDJOURNEY',
    'WERYAI_IMAGE_1_0',
]
const props = withDefaults(
    defineProps<{
        type: any
        create_btn_disable: boolean
        create_loading: boolean
        recommend_list: Array<any>
    }>(),
    {
        type: 'text_to_image',
        create_btn_disable: false,
        create_loading: false,
        recommend_list: () => [],
    }
)

const edit_model = defineModel('edit_model', {
    type: String,
    default: '',
})

const edit_prompt = defineModel('edit_prompt', {
    type: String,
    default: '',
})

const edit_style = defineModel('edit_style', {
    type: String,
    default: 'auto',
})

const edit_aspect_ratio = defineModel('edit_aspect_ratio', {
    type: String,
    default: '',
})

const edit_output_image_number = defineModel('edit_output_image_number', {
    type: Number,
    default: 1,
})

const batch_generate = defineModel('batch_generate', {
    type: Boolean,
    default: false,
})

const public_visible = defineModel('public_visible', {
    type: Boolean,
    default: true,
})
const copyright_protection = defineModel('copyright_protection', {
    type: Boolean,
    default: false,
})

const edit_model_list = defineModel('edit_model_list', {
    type: Array<string>,
    default: () => [],
})

const image_size = defineModel('image_size', {
    type: String,
    default: '',
})

const use_web_search = defineModel('use_web_search', {
    type: Boolean,
    default: false,
})

const generate_type = defineModel('generate_type', {
    type: String,
    default: 'standard',
})

const has_web_search = computed(() => {
    return (
        edit_model.value === 'CHATBOT_GEMINI_3_PRO_IMAGE_PREVIEW' &&
        !batch_generate.value
    )
})
const batch_num_images = [1, 2, 3, 4]
const aspectRatioList = ref<Array<any>>([])
const num_images = ref<Array<any>>(batch_num_images)
const resolutionList = ref<Array<any> | null>(null)
const prompt_length_limit = ref<number>(5000)

const cost_model = computed(() => {
    return batch_generate.value
        ? edit_model_list.value?.join(',')
        : edit_model.value
})

const modelList = computed(() => {
    if (!modelData.value) return []
    const arr = modelData.value?.image_models?.[props.type] || {}

    // 只有当 edit_model 完全为空且没有设置任何值时，才设置默认值

    if (!edit_model_list.value?.length) {
        edit_model_list.value = [arr[0]?.model_key || '']
    }

    return arr
})

const multiModelList = computed(() => {
    // 只在模型对比模式下过滤
    return modelList.value?.filter(
        (it: any) => !MODEL_COMPARE_EXCLUDE_LIST.includes(it.model_key)
    )
})

const isAllowedInModelCompare = (modelKey?: string) => {
    if (!modelKey) return false
    return !MODEL_COMPARE_EXCLUDE_LIST.includes(modelKey)
}

const uniqKeepOrder = (arr: string[]) => {
    const res: string[] = []
    for (const it of arr) {
        if (!it) continue
        if (res.includes(it)) continue
        res.push(it)
    }
    return res
}

const getFirstAllowedCompareModel = () => {
    // 优先用过滤后的列表作为兜底（保证可选）
    const first = multiModelList.value?.[0]?.model_key
    if (first) return first
    // 再兜底：从全量列表里找一个不在排除名单的
    const fromAll = modelList.value?.find((it: any) =>
        isAllowedInModelCompare(it?.model_key)
    )?.model_key
    return fromAll || ''
}

// 标准模式 -> 模型对比：先清空对比已选，再把当前标准模型带入多选，并过滤掉排除模型
const syncStandardToCompare = () => {
    const standardModel = edit_model.value
    // 需求：标准 -> 模型对比时，不保留之前模型对比的选中值
    // 所以这里不合并旧的 edit_model_list，只用标准模型来初始化
    const next = uniqKeepOrder([standardModel].filter(isAllowedInModelCompare))

    // 如果过滤后为空，则用兜底模型
    if (!next.length) {
        const fallback = getFirstAllowedCompareModel()
        if (fallback) next.push(fallback)
    }

    // 写回：对比多选 & 作为“主模型”的 edit_model（用于尺寸/张数等联动）
    if (JSON.stringify(edit_model_list.value || []) !== JSON.stringify(next)) {
        edit_model_list.value = next
    }
    if (next[0] && edit_model.value !== next[0]) {
        edit_model.value = next[0]
    }
}

// 模型对比 -> 标准模式：保留一个模型回填给标准模型
const syncCompareToStandard = () => {
    const list = Array.isArray(edit_model_list.value)
        ? edit_model_list.value
        : []
    const first = list.find(Boolean) || ''
    if (first && edit_model.value !== first) {
        edit_model.value = first
    }
}

// 根据模型和尺寸更新比例列表的函数
const updateAspectRatioList = () => {
    if (!edit_model.value || !modelData.value) return

    let same_ = modelData.value?.image_models?.[props.type]?.find(
        (it: any) => it.model_key === edit_model.value
    )

    if (!same_) return

    // 如果模型是 DREAMINA_4 且尺寸是 1k，则只显示 1:1 比例
    if (
        ['DREAMINA_4'].includes(edit_model.value) &&
        image_size.value === '1k'
    ) {
        aspectRatioList.value = ['1:1']
        edit_aspect_ratio.value = '1:1'
    } else {
        // 其他情况使用原始的比例列表
        aspectRatioList.value = same_?.image_sizes || []
        // 如果父组件已经通过本地缓存恢复了宽高比，则不要用默认值覆盖
        if (!edit_aspect_ratio.value) {
            edit_aspect_ratio.value = aspectRatioList.value?.[0] || ''
        } else {
            let same_aspect_ratio = aspectRatioList.value?.find(
                (it: string) => it === edit_aspect_ratio.value
            )
            if (!same_aspect_ratio) {
                edit_aspect_ratio.value = aspectRatioList.value?.[0] || ''
            }
        }
    }
}

watch(
    [() => edit_model.value, () => modelData.value, () => generate_type.value],
    ([newVal, newVal2, newVal3]: [string, any, string]) => {
        if (newVal && newVal2 && newVal3) {
            let same_ = newVal2?.image_models?.[props.type]?.find(
                (it: any) => it.model_key === newVal
            )
            num_images.value =
                newVal3 === 'batch' ? batch_num_images : same_?.num_images
            resolutionList.value = same_?.resolutions
            image_size.value = resolutionList.value?.[0] || ''
            edit_output_image_number.value = num_images.value?.[0] || 1
            prompt_length_limit.value = same_?.prompt_length_limit || 5000

            // 如果模型只支持一个输出数量，自动设置为该值（如 Midjourney 只支持 4）
            if (num_images.value?.length === 1) {
                edit_output_image_number.value = num_images.value[0]
            }

            // 调用更新比例列表的函数
            updateAspectRatioList()
        }
    },
    {
        immediate: true,
        flush: 'post',
    }
)

// 监听 image_size 变化，当尺寸变化时也需要重新判断比例
watch(
    () => image_size.value,
    () => {
        updateAspectRatioList()
    }
)

// 对比模式下：保证 edit_model 始终等于多选的第一项（用于分辨率/张数等联动）
watch(
    () => edit_model_list.value,
    newList => {
        if (!batch_generate.value) return
        if (!Array.isArray(newList)) return

        const normalized = uniqKeepOrder(
            newList.filter(isAllowedInModelCompare)
        )
        const fallback = getFirstAllowedCompareModel()
        // 对比模式不允许空：用户清空时自动补回一个可用模型
        const ensured = normalized.length
            ? normalized
            : fallback
              ? [fallback]
              : []

        if (JSON.stringify(newList) !== JSON.stringify(ensured)) {
            edit_model_list.value = ensured
        }

        const first = ensured[0] || ''
        if (first && edit_model.value !== first) {
            edit_model.value = first
        }
    },
    { deep: true }
)

const batch_generate_aspect_ratio_list = [
    '9:16',
    '1:1',
    '16:9',
    '3:2',
    '2:3',
    '3:4',
    '4:3',
]
watch(
    () => batch_generate.value,
    newVal => {
        if (newVal) {
            // 进入模型对比：先清空对比已选，再同步标准模式选择（并过滤排除模型）
            edit_model_list.value = []
            syncStandardToCompare()
            // 如果父组件已经从本地缓存恢复了数量，则保留；否则使用默认值 1
            if (!edit_output_image_number.value) {
                edit_output_image_number.value = 1
            }
            aspectRatioList.value = batch_generate_aspect_ratio_list
            // 有缓存的宽高比时优先使用缓存值
            if (!edit_aspect_ratio.value) {
                edit_aspect_ratio.value = '9:16'
            }
            // 仅在当前没有多选模型时设置默认值，避免覆盖已恢复的多选配置
            if (!edit_model_list.value || !edit_model_list.value.length) {
                // 再兜底一次，确保对比模式下始终有一个可用模型（且已过滤）
                syncStandardToCompare()
            }
        } else {
            // 退出模型对比：保留一个模型回填到标准模式
            syncCompareToStandard()
            // 关闭批量时也只在没有缓存值的情况下设置默认
            if (!edit_output_image_number.value) {
                edit_output_image_number.value = 1
            }
            if (!edit_aspect_ratio.value) {
                edit_aspect_ratio.value = aspectRatioList.value?.[0] || ''
            }
            // edit_model.value = edit_model.value || ''
        }
    }
)
</script>
<style scoped lang="less">
.setting-side {
    width: 100%;
    height: 100%;
    background-color: #222;
}

:deep(.el-switch__core) {
    width: 52px;
    height: 28px;
    border-radius: 29px;
}
</style>
